import imageCompression from 'browser-image-compression';

export const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
export const MAX_IMAGE_SIZE_BYTES = 0.5 * 1024 * 1024; // 0.5 MB

// Signatures must stay small: their base64 content is embedded directly in the
// PDF-generation payload, so oversized signatures caused 413 errors (PROD-463).
// These limits are intentionally stricter than the generic attachment ones.
export const SIGNATURE_MAX_SIZE_MB = 0.02; // ~20 KB
export const SIGNATURE_MAX_WIDTH_OR_HEIGHT = 800; // px

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

export function isImageFileName(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return IMAGE_EXTENSIONS.includes(ext);
}

const ZIP_EXTENSIONS = ['zip', 'rar', '7z', 'tar', 'gz'];

export function isZipFileName(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ZIP_EXTENSIONS.includes(ext);
}

export function getMimeTypeFromFileName(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const mimeMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
  };
  return mimeMap[ext] || 'application/octet-stream';
}

export interface ValidateFileResult {
  file: File;
  compressed: boolean;
}

export async function validateAndPrepareFile(
  file: File
): Promise<ValidateFileResult> {
  const isImage = isImageFile(file);

  if (!isImage && file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error('fileTooLarge');
  }

  if (isImage && file.size > MAX_IMAGE_SIZE_BYTES) {
    const compressed = await imageCompression(file, {
      maxSizeMB: 0.5,
      useWebWorker: true,
    });

    if (compressed.size > MAX_IMAGE_SIZE_BYTES) {
      throw new Error('fileTooLargeAfterCompression');
    }

    return { file: compressed as File, compressed: true };
  }

  return { file, compressed: false };
}

/**
 * Convert a base64 data URL (e.g. produced by a signature canvas) into a File,
 * so it can be fed through the same image-compression pipeline used for uploads.
 */
export function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [header, base64 = ''] = dataUrl.split(',');
  const mime = header.match(/data:(.*?);base64/)?.[1] || 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: mime });
}

/**
 * Read a File and return only the base64 payload (without the data URL prefix).
 */
export function fileToBase64Content(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('readError'));
    reader.readAsDataURL(file);
  });
}

/**
 * Always resize/compress a signature image so its resulting payload stays small,
 * regardless of the source (file upload or canvas drawing). This prevents the
 * combined PDF-generation request from exceeding the server payload limit.
 */
export async function prepareSignatureImage(file: File): Promise<File> {
  const compressed = await imageCompression(file, {
    maxSizeMB: SIGNATURE_MAX_SIZE_MB,
    maxWidthOrHeight: SIGNATURE_MAX_WIDTH_OR_HEIGHT,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.7,
  });
  return compressed as File;
}
